import { useState, useCallback, useRef } from "react";
import { Box, Button, Collapse, Paper, Skeleton, Text } from "@mantine/core";
import {
  IconFileText,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { useMedplum } from "@medplum/react";
import { fetchTranscriptForComposition } from "@benchmark/core/services";
import { compositionTranscriptEnvOptions } from "@/utils/fhir/composition-runtime-config";
import styles from "./TranscriptViewer.module.css";

/**
 * Props for the TranscriptViewer component.
 * @agentic connections
 */
export type TranscriptViewerProps = {
  /** Dialpad call ID to look up the transcript Communication */
  dialpadCallId: string;
};

/**
 * Collapsible panel that lazily fetches and displays a call transcript.
 * @agentic connections
 */
export const TranscriptViewer = ({ dialpadCallId }: TranscriptViewerProps) => {
  const medplum = useMedplum();
  const [isOpen, setIsOpen] = useState(false);
  const [transcript, setTranscript] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  // Resolved empty vs transcript for a specific call id (avoids effect reset when dialpadCallId changes).
  const [emptyResolution, setEmptyResolution] = useState<{
    callId: string;
    isEmpty: boolean;
  } | null>(null);
  const cacheRef = useRef<Map<string, string | undefined>>(new Map());

  const showNoTranscriptMessage =
    !isLoading &&
    !transcript &&
    emptyResolution?.callId === dialpadCallId &&
    emptyResolution.isEmpty;

  const handleToggle = useCallback(async () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);

    if (!nextOpen) return;

    if (cacheRef.current.has(dialpadCallId)) {
      const cached = cacheRef.current.get(dialpadCallId);
      setTranscript(cached);
      setEmptyResolution({
        callId: dialpadCallId,
        isEmpty: cached === undefined,
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await fetchTranscriptForComposition(
        medplum,
        dialpadCallId,
        compositionTranscriptEnvOptions(),
      );
      cacheRef.current.set(dialpadCallId, result);
      setTranscript(result);
      setEmptyResolution({
        callId: dialpadCallId,
        isEmpty: result === undefined,
      });
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, dialpadCallId, medplum]);

  const ToggleIcon = isOpen ? IconChevronUp : IconChevronDown;

  return (
    <Box>
      <Button
        variant="subtle"
        size="compact-xs"
        className={styles.toggleButton}
        leftSection={<IconFileText size={14} />}
        rightSection={<ToggleIcon size={14} />}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls="transcript-panel"
      >
        {isOpen ? "Hide Transcript" : "Show Transcript"}
      </Button>

      <Collapse in={isOpen}>
        <Paper
          id="transcript-panel"
          p="sm"
          mt="xs"
          radius="md"
          withBorder
          className={styles.transcriptPanel}
          role="region"
          aria-label="Call transcript"
        >
          {isLoading && (
            <>
              <Skeleton height={12} mb={6} />
              <Skeleton height={12} mb={6} />
              <Skeleton height={12} mb={6} width="70%" />
            </>
          )}
          {!isLoading && transcript && (
            <Text
              component="pre"
              size="xs"
              m={0}
              style={{ fontFamily: "inherit" }}
            >
              {transcript}
            </Text>
          )}
          {showNoTranscriptMessage && (
            <Text size="xs" c="dimmed" fs="italic">
              No transcript available for this call.
            </Text>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};
